import { Box, TextField, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import useColors from "../../api/hooks/useColors";
import RedAsterisk from "../../components/RedAsterisk";

const TenantSLAField = () => {
  const colors = useColors();
  const { values, setFieldValue, errors, touched } = useFormikContext();

  const SLA_ITEMS = [
    {
      label: "Critical",
      color: colors.severity_critical_hover,
    },
    {
      label: "High",
      color: colors.severity_high_hover,
    },
    {
      label: "Medium",
      color: colors.severity_medium_hover,
    },
    {
      label: "Low",
      color: colors.severity_low_hover,
    },
  ];

  const handleNumericInput = (e, fieldName) => {
    const value = e.target.value;
    // Only update if input is a number or empty
    if (/^[0-9]*$/.test(value)) {
      setFieldValue(fieldName, value === "" ? undefined : Number(value));
    }
  };

  // Prevent '+' and '-' characters from being entered
  const preventPlusMinusInput = (e) => {
    if (e.key === "+" || e.key === "-") {
      e.preventDefault();
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Title and description */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        <Box sx={{ display: "flex", gap: "2px", alignItems: "center" }}>
          <Typography variant="caption">Set your SLA commitment</Typography>
          <RedAsterisk />
        </Box>
        <Typography variant="caption" color={colors.text_secondary}>
          Specify how many days are committed to resolving issues.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {SLA_ITEMS.map((item) => (
          <Box
            key={item.label}
            sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            <Typography variant="caption" color={colors.text_secondary}>
              {item.label}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
                pl: 1,
                pb: 0.5,
              }}
            >
              {/* Chip color-dot */}
              <Box
                sx={{
                  backgroundColor: item.color,
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                }}
              />
              <TextField
                variant="standard"
                type="number"
                inputProps={{
                  min: 0,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  style: {
                    textAlign: "center",
                    color: colors.text_secondary,
                    fontSize: "0.875rem",
                  },
                }}
                onKeyDown={preventPlusMinusInput}
                value={values.sla?.[item.label] || item.defaultValue}
                onChange={(e) => handleNumericInput(e, `sla.${item.label}`)}
                sx={{
                  minWidth: "40px",
                  width: "40px",
                  height: "24px",
                  "& .MuiInput-underline:before": {
                    borderBottom: `1px solid ${colors.text_field_standard_enabled_stroke}`,
                  },
                  "& .MuiInput-underline:hover:before": {
                    borderBottom: `1px solid ${colors.text_field_standard_enabled_stroke}`,
                  },
                  "& .MuiInput-underline:after": {
                    borderBottom: `1px solid ${colors.text_field_standard_enabled_stroke}`,
                  },
                  // Remove spinner buttons from number input
                  "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
                    {
                      "-webkit-appearance": "none",
                      margin: 0,
                    },
                  "& input[type=number]": {
                    "-moz-appearance": "textfield",
                  },
                }}
              />
              <Typography variant="caption" sx={{ ml: 0.5 }}>
                Days
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Field-specific error messages */}
      {errors.sla &&
        touched.sla &&
        typeof errors.sla === "object" &&
        SLA_ITEMS.map(
          (item) =>
            errors.sla[item.label] && (
              <Typography
                key={`error-${item.label}`}
                variant="caption"
                sx={{ color: colors.error_500 }}
              >
                {errors.sla[item.label]}
              </Typography>
            )
        )}
    </Box>
  );
};

export default TenantSLAField;
